import React, { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { toLocalDateString } from '../../utils/dates';
import { useEscapeToClose } from './useEscapeToClose';

/** Widest the two-month panel gets, used to keep it inside the viewport. */
const PANEL_WIDTH = 560;

/** Gap between the trigger and the floating panel. */
const PANEL_OFFSET = 8;

/** Panel at its tallest, used to decide whether to flip upwards. */
const PANEL_MAX_HEIGHT = 380;

/** Six rows, always, so the panel does not resize as months are browsed. */
const GRID_CELLS = 42;

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

/** Monday first: `getDay()` counts from Sunday, and Chile does not. */
const WEEKDAYS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

/**
 * `YYYY-MM-DD` into a local `Date` at midnight.
 *
 * Built from the parts on purpose: `new Date('2026-09-07')` is read as an ISO
 * instant, so it lands on UTC midnight and Chile gets the 6th. It is the same
 * class of bug `toLocalDateString` exists to avoid on the way out.
 *
 * @param {string} value
 * @returns {Date|null}
 */
const parseLocalDate = (value) => {
  if (!value || typeof value !== 'string') return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);

const addMonths = (date, count) =>
  new Date(date.getFullYear(), date.getMonth() + count, 1);

/** `DD/MM/YYYY`, which is what the input has always displayed. */
const toDisplayDate = (value) => {
  const date = parseLocalDate(value);
  if (!date) return '';
  return `${String(date.getDate()).padStart(2, '0')}/${String(
    date.getMonth() + 1
  ).padStart(2, '0')}/${date.getFullYear()}`;
};

/**
 * Which month sits on the **right**.
 *
 * The current one by default, so the pair opens as `anterior | actual`. A
 * shipping filter looks backwards: anchoring on the current month spends half
 * the calendar on days that have not happened, and puts every past date one
 * arrow click away.
 *
 * A range that ends outside that pair moves the anchor to its end rather than
 * its start — the end is the edge somebody reopening the picker is about to
 * move.
 *
 * @param {string} endValue - `YYYY-MM-DD`, or `''`
 * @returns {Date} First day of the right-hand month.
 */
const resolveAnchor = (endValue) => {
  const currentMonth = startOfMonth(new Date());
  const end = parseLocalDate(endValue);
  if (!end) return currentMonth;

  const endMonth = startOfMonth(end);
  const previousMonth = addMonths(currentMonth, -1);
  if (endMonth > currentMonth || endMonth < previousMonth) return endMonth;
  return currentMonth;
};

/**
 * The cells of one month grid: the tail of the previous month, the month
 * itself, and enough of the next one to close the sixth row.
 *
 * @param {Date} monthStart
 * @returns {Array<{ key: string, date: Date, isCurrentMonth: boolean }>}
 */
const buildMonthGrid = (monthStart) => {
  const year = monthStart.getFullYear();
  const month = monthStart.getMonth();
  // `getDay()` is 0 on Sunday; the grid starts on Monday.
  const leading = (monthStart.getDay() + 6) % 7;

  return Array.from({ length: GRID_CELLS }, (_, index) => {
    const date = new Date(year, month, index - leading + 1);
    return {
      key: toLocalDateString(date),
      date,
      isCurrentMonth: date.getMonth() === month,
    };
  });
};

/**
 * One month: its header, the weekday row and the day grid.
 *
 * Days are compared as `YYYY-MM-DD` strings rather than as `Date`s. That is the
 * currency the filters and the backend already speak, and string comparison
 * orders ISO dates correctly without dragging in time-of-day or a timezone.
 */
const MonthGrid = ({
  monthStart,
  selection,
  today,
  onDayClick,
  onDayHover,
  onPrevious,
  onNext,
}) => {
  const cells = useMemo(() => buildMonthGrid(monthStart), [monthStart]);

  return (
    <div className="flex-1 min-w-0">
      <div className="flex items-center justify-between mb-3 h-8">
        {onPrevious ? (
          <button
            type="button"
            onClick={onPrevious}
            aria-label="Mes anterior"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-text-muted hover:bg-ninja-gray dark:hover:bg-[#2d2d2d] hover:text-ninja-dark dark:hover:text-white transition-colors"
          >
            <ChevronLeft size={16} />
          </button>
        ) : (
          <span className="w-8" />
        )}

        <span className="text-sm font-bold text-ninja-dark dark:text-white">
          {MONTHS[monthStart.getMonth()]} {monthStart.getFullYear()}
        </span>

        {onNext ? (
          <button
            type="button"
            onClick={onNext}
            aria-label="Mes siguiente"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-text-muted hover:bg-ninja-gray dark:hover:bg-[#2d2d2d] hover:text-ninja-dark dark:hover:text-white transition-colors"
          >
            <ChevronRight size={16} />
          </button>
        ) : (
          <span className="w-8" />
        )}
      </div>

      <div className="grid grid-cols-7">
        {WEEKDAYS.map((day) => (
          <div
            key={day}
            className="h-8 flex items-center justify-center text-xs font-semibold text-text-muted"
          >
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {cells.map(({ key, date, isCurrentMonth }) => {
          const isEdge =
            (Boolean(selection.start) && key === selection.start) ||
            (Boolean(selection.end) && key === selection.end);

          // The fill stops at the month's own days. Without the
          // `isCurrentMonth` guard the last row of August paints the first
          // days of September, which the September grid then paints again:
          // the same six days filled twice, and a month block that ends on
          // days belonging to the next one. The end chips are not gated the
          // same way, so clicking a day bleeding in from a neighbouring month
          // still answers with something.
          const inRange =
            isCurrentMonth &&
            Boolean(selection.start) &&
            Boolean(selection.end) &&
            key > selection.start &&
            key < selection.end;

          const isToday = key === today;

          return (
            <button
              key={key}
              type="button"
              onClick={() => onDayClick(date)}
              onMouseEnter={() => onDayHover(key)}
              aria-label={`${date.getDate()} de ${MONTHS[date.getMonth()]} de ${date.getFullYear()}`}
              aria-pressed={isEdge}
              className={clsx(
                'h-9 w-full text-sm transition-colors',
                // The middle of a range keeps square edges so the cells join up;
                // only the two ends are rounded.
                isEdge && 'rounded-lg bg-ninja-green text-white font-bold',
                // Its own token rather than `ninja-green/15`: the palette maps
                // every colour to a bare `var(--x)`, and Tailwind cannot derive
                // an alpha from that, so the whole `/NN` family compiles to
                // nothing at all.
                inRange && 'bg-rangeHighlight',
                !isEdge && !inRange && 'rounded-lg hover:bg-ninja-gray dark:hover:bg-[#2d2d2d]',
                // Exactly one text colour per cell, chosen here rather than by
                // stacking classes. Two of these on the same element have equal
                // specificity, so the winner would be whichever Tailwind emits
                // last — which is why today came out green over a range in the
                // light theme and white in the dark one.
                !isEdge &&
                  (isToday
                    ? // Marked apart from the selection: when a range ends today
                      // the two coincide and nothing would tell them apart.
                      'font-bold text-ninja-green'
                    : isCurrentMonth
                      ? 'text-ninja-dark dark:text-white'
                      : // Bleeding in from the neighbouring month: still
                        // clickable, just visibly secondary.
                        'text-text-muted')
              )}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/**
 * The date range every listing filters by.
 *
 * Speaks `YYYY-MM-DD` on both ends, which is what the backend expects and what
 * the slices hold.
 *
 * **Opens on `mes anterior | mes actual`**, which is why this is written by hand
 * rather than wrapping `react-tailwindcss-datepicker` as it used to.
 * That library anchors its left calendar on the month of the selected start
 * date — or on today when nothing is selected — and offers no way to shift it:
 * `startFrom` is overridden by its own effect whenever a range is set, and the
 * component exposes no ref to reach the internal `updateFirstDate`. Every
 * listing here opens with a range already applied, so it always showed
 * `actual | siguiente`, spending half the calendar on the future.
 *
 * Dropping it also fixed the month and weekday names, which came out in English
 * with the week starting on Sunday.
 *
 * @param {object} props
 * @param {string} props.startDate - `YYYY-MM-DD`, or `''`
 * @param {string} props.endDate - `YYYY-MM-DD`, or `''`
 * @param {(range: { start_date: string, end_date: string }) => void} props.onChange
 * @param {string} [props.containerClassName] - For the wrapper's width.
 */
export const DateRangePicker = ({
  startDate,
  endDate,
  onChange,
  containerClassName = 'relative min-w-[260px]',
}) => {
  const triggerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState(null);
  const [anchor, setAnchor] = useState(() => resolveAnchor(endDate));

  // The first end of a range being drawn. While it is set the picker is
  // mid-selection and paints against the hovered day instead of the props.
  const [pendingStart, setPendingStart] = useState(null);
  const [hovered, setHovered] = useState(null);

  const today = useMemo(() => toLocalDateString(new Date()), []);

  const close = () => {
    setIsOpen(false);
    setPendingStart(null);
    setHovered(null);
  };

  const open = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;

    const spaceBelow = window.innerHeight - rect.bottom;
    const flipUp = spaceBelow < PANEL_MAX_HEIGHT && rect.top > spaceBelow;

    setPosition({
      // Clamped so a trigger near the right edge does not push the panel off
      // screen; it is much wider than the input that opens it.
      left: Math.max(8, Math.min(rect.left, window.innerWidth - PANEL_WIDTH - 8)),
      top: flipUp ? undefined : rect.bottom + PANEL_OFFSET,
      bottom: flipUp ? window.innerHeight - rect.top + PANEL_OFFSET : undefined,
    });
    setAnchor(resolveAnchor(endDate));
    setPendingStart(null);
    setHovered(null);
    setIsOpen(true);
  };

  useEscapeToClose(isOpen, close);

  const handleDayClick = (date) => {
    const value = toLocalDateString(date);

    if (!pendingStart) {
      setPendingStart(value);
      setHovered(null);
      return;
    }

    // Drawing a range backwards is a normal thing to do; the ends just swap
    // instead of the click being rejected.
    const [start_date, end_date] =
      pendingStart <= value ? [pendingStart, value] : [value, pendingStart];

    // Emitted once, with both ends. Firing on the first click would send a
    // half-drawn range to the backend and cost a request per click.
    onChange({ start_date, end_date });
    close();
  };

  const handleClear = (event) => {
    event.stopPropagation();
    onChange({ start_date: '', end_date: '' });
    close();
  };

  // Only while a range is being drawn. Outside that the hovered day changes
  // nothing on screen, and tracking it would re-render all 84 cells per cell
  // the pointer crosses.
  const handleDayHover = (value) => {
    if (pendingStart) setHovered(value);
  };

  const selection = useMemo(() => {
    if (pendingStart) {
      const other = hovered || pendingStart;
      return pendingStart <= other
        ? { start: pendingStart, end: other }
        : { start: other, end: pendingStart };
    }
    return { start: startDate || '', end: endDate || '' };
  }, [pendingStart, hovered, startDate, endDate]);

  const hasValue = Boolean(startDate || endDate);
  const displayText = hasValue
    ? `${toDisplayDate(startDate)} ~ ${toDisplayDate(endDate)}`
    : '';

  const panel = position && (
    <>
      {/* Outside clicks close through a full-screen overlay rather than a
          document listener, matching the rest of the UI. It closes on wheel too:
          the panel is positioned from the trigger's box and does not follow it
          as the page scrolls. */}
      <div className="fixed inset-0 z-[10000]" onClick={close} onWheel={close} />
      <div
        role="dialog"
        aria-label="Seleccionar rango de fechas"
        style={{
          position: 'fixed',
          top: position.top,
          bottom: position.bottom,
          left: position.left,
        }}
        className="z-[10001] bg-white dark:bg-[#1a1a1a] border border-border shadow-2xl rounded-2xl p-4 max-h-[80vh] overflow-y-auto"
        onMouseLeave={() => setHovered(null)}
      >
        <div className="flex flex-col sm:flex-row gap-6 sm:w-[520px]">
          <MonthGrid
            monthStart={addMonths(anchor, -1)}
            selection={selection}
            today={today}
            onDayClick={handleDayClick}
            onDayHover={handleDayHover}
            onPrevious={() => setAnchor(addMonths(anchor, -1))}
          />
          <MonthGrid
            monthStart={anchor}
            selection={selection}
            today={today}
            onDayClick={handleDayClick}
            onDayHover={handleDayHover}
            onNext={() => setAnchor(addMonths(anchor, 1))}
          />
        </div>

        {pendingStart && (
          <p className="text-xs text-text-secondary text-center mt-3 pt-3 border-t border-border">
            Elige la fecha de término
          </p>
        )}
      </div>
    </>
  );

  // `z-30`: above the cards, below the sticky header. At `z-50` it outranked
  // the header's `z-40`, so scrolling drew the date input over the page title.
  return (
    <div className="flex-shrink-0 z-30">
      <div className={containerClassName}>
        <button
          type="button"
          ref={triggerRef}
          onClick={() => (isOpen ? close() : open())}
          className="w-full bg-white dark:bg-[#1a1a1a] border border-border rounded-xl pl-4 pr-10 py-2 text-sm font-medium outline-none text-left text-ninja-dark dark:text-white transition-colors cursor-pointer hover:bg-ninja-gray dark:hover:bg-[#222]"
        >
          {displayText || (
            <span className="text-text-muted">Selecciona un rango</span>
          )}
        </button>

        {hasValue ? (
          <button
            type="button"
            onClick={handleClear}
            aria-label="Limpiar el rango de fechas"
            className="absolute right-0 top-0 h-full px-3 flex items-center text-text-muted hover:text-danger transition-colors"
          >
            <X size={16} />
          </button>
        ) : (
          <span className="absolute right-0 top-0 h-full px-3 flex items-center text-text-muted pointer-events-none">
            <Calendar size={16} />
          </span>
        )}
      </div>

      {isOpen && createPortal(panel, document.body)}
    </div>
  );
};
